import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
// 「내가 대표인가」 — 「우리 조직」·「전사 근태」와 같은 축(2026-09-01).
import { getMyDutyScope } from './my-org-scope';

/**
 * **이 사람에게 이 메뉴를 보여도 되는가** — 레일·전체메뉴·즐겨찾기가 **한 판정**을 쓴다.
 *
 * ★★ **왜 제 파일인가** (2026-09-01). 종전에는 `menu-catalog.ts` 에 있었는데, 그 파일은
 *    `NavBar` 에서 `PRIMARY_NAV` 를 가져온다. 그래서 `NavBar` 가 이 판정을 쓰려는 순간
 *    **순환 import** 가 된다(런타임에 `undefined` 가 될 수 있는 부류다). 둘 다 여기를 본다.
 * ⚠ 이건 **보이기 규칙**이지 보안 경계가 아니다 — 권위 있는 차단은 서버가 한다.
 *   두 기준이 갈리면 「메뉴는 있는데 열면 403」이 된다.
 */

/**
 * ★★ 관리자에게만 보이는 메뉴 — **경로로 판정한다.**
 *
 * 통합설정(`/settings`)은 2026-08-25 부터 **회사 관리자 화면**이다. 키(`menu_key='settings'`)가
 * 아니라 경로로 잠그는 것은, 그 행의 url 이 `/me`(내 프로필)로 마이그레이션된 이력이 있어
 * 키로 잠그면 **내 프로필 진입점까지** 관리자 전용이 되기 때문이다.
 *
 * ★ 인사(`/erp/hr`)는 **여기 없다** — 직원도 그 메뉴로 «내 인사» 를 본다.
 */
export const ADMIN_ONLY_MENU_PATHS: ReadonlySet<string> = new Set(['/settings']);

/**
 * ★★★ **관리자 «또는 대표» 에게만 보이는 메뉴** (2026-09-01 · 「업무보고」).
 *
 *    `/settings` 처럼 관리자 전용으로 잠그면 **대표가 못 본다** — 대표는 역할이 `STAFF` 일 수
 *    있고(근태 담당자가 그랬던 것과 같은 사정), 그러면 **정작 볼 사람이 메뉴를 잃는다.**
 *    메뉴 권한(`menu_access_rules`)으로도 못 한다 — 그 규칙은 **부서 단위**뿐이라 「대표 한
 *    사람」을 지정할 수 없다(그 파일이 `user`·`role` 대상을 일부러 뺐다).
 *
 * ★ 그래서 「우리 조직」·「전사 근태」와 **같은 축**을 쓴다 — `/users/me` 의 `isRepresentative`.
 * ★ 서버도 같은 기준으로 막는다(`/api/v1/briefing/summary` → 대표 또는 관리자).
 */
export const EXEC_MENU_PATHS: ReadonlySet<string> = new Set(['/briefing']);

export function canSeeMenuPath(to: string, isAdmin: boolean, isRepresentative = false): boolean {
  if (ADMIN_ONLY_MENU_PATHS.has(to)) return isAdmin;
  if (EXEC_MENU_PATHS.has(to)) return isAdmin || isRepresentative;
  return true;
}

/** 로그인한 사람이 회사 관리자인가 — 보이기 판정용(서버가 최종 판정). */
export function useIsAdminViewer(): boolean {
  const role = useAuthStore((s) => s.user?.role);
  return role === 'ADMIN' || role === 'SYSTEM_ADMIN';
}

/**
 * 내가 대표인가 — 보이기 판정용(서버가 최종 판정).
 *
 * ★ 「최초 1회」 모듈 캐시(`getMyDutyScope`)를 쓴다 — 레일·전체메뉴·즐겨찾기가 각자 물으면
 *   같은 답을 세 번 받는다. **로그아웃하면 그 캐시가 비워진다**(`session-caches`).
 */
export function useIsRepresentativeViewer(): boolean {
  const [is, setIs] = useState(false);
  useEffect(() => {
    let alive = true;
    getMyDutyScope().then((s) => { if (alive) setIs(s.isRepresentative); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  return is;
}
